import type { ValueType } from "./graph-utils/GraphTypes";
import MultiRegExp, { type ExecGroupAllResult } from "./MultiRegExp";

export interface MultiRegExpMatch {
  match: string;
  start: number;
  end: number;
}

export type ExplicitOrder = Record<string, 1 | 0 | -1>;

export interface DefinitionMatcher {
  execForAllGroups: (input: string) => ExecGroupAllResult | null;
}

interface CommandOptions {
  id: string;
  alias?: string[];
  description?: string;
  displayName: string;
  arguments: Argument[];
  returnType?: ValueType | ValueType[];
  naturalPriority?: number;
  hidden?: boolean;

  /**
   * A list of regexs to match against
   *
   * If you want to change the order of the arguments, use named capture groups like this:
   * /(?<$2>.+) years after (?<$1>.+)$/
   * The order is then $1, $2, $3, etc.
   */
  definition?: Array<RegExp | DefinitionMatcher | ((input: string) => ExecGroupAllResult | null)>;
}

export const SEPARATOR_REGEX = /[_\-]/g;

export default class Command {
  definition: DefinitionMatcher[];
  id: string;
  displayName: string;
  arguments: Argument[];
  returnTypes: ValueType[];
  minimumArguments: number;
  maximumArguments: number;
  names: Set<string>;
  description: string | undefined;
  naturalPriority: number;
  hidden;

  constructor(options: CommandOptions) {
    this.id = options.id;
    this.arguments = options.arguments;
    this.minimumArguments = options.arguments.filter((arg) => arg.required).length;
    this.maximumArguments = options.arguments[options.arguments.length - 1]?.variadic
      ? Number.POSITIVE_INFINITY
      : options.arguments.length;
    this.definition = (options.definition
      ? options.definition.map((def) => {
        if (def instanceof RegExp) {
          return new MultiRegExp(def);
        }
        if (typeof def === "function") {
          return { execForAllGroups: def } as DefinitionMatcher;
        }
        return def as DefinitionMatcher;
      })
      : []) as DefinitionMatcher[];
    this.names = new Set([this.id, this.id.replace(SEPARATOR_REGEX, ""), ...(options.alias || [])]);
    this.description = options.description;
    this.displayName = options.displayName;
    this.returnTypes = Array.isArray(options.returnType) ? options.returnType : [options.returnType || "auto"];
    this.naturalPriority = options.naturalPriority ?? 0;
    this.hidden = options.hidden || false;
  }

  toJSON() {
    return { ...this, definition: undefined };
  }
}

export type ArgumentType = "relationship" | "entity" | "alias" | ValueType;

export interface ArgumentOptions {
  name: string;
  types: ArgumentType | ArgumentType[];
  required: boolean;
  variadic?: boolean;
}

export class Argument {
  name: string;
  types: ArgumentType[];
  required: boolean;
  variadic: boolean;

  constructor(options: ArgumentOptions) {
    this.name = options.name;
    this.types = Array.isArray(options.types) ? options.types : [options.types];
    this.required = options.required;
    this.variadic = options.variadic || false;
  }

  static required(
    name: string,
    types: ArgumentType | ArgumentType[],
    options?: Omit<Partial<ArgumentOptions>, "name" | "types" | "required">,
  ) {
    return new Argument({ ...options, name, types, required: true });
  }

  static optional(
    name: string,
    types: ArgumentType | ArgumentType[],
    options?: Omit<Partial<ArgumentOptions>, "name" | "types" | "required">,
  ) {
    return new Argument({ ...options, name, types, required: false });
  }
}
